import { Navigate, Route, Routes } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import Logout from '@/pages/Logout.tsx';
import NotFound from '@/pages/NotFound.tsx';
import Automations from '@/pages/Automations/Automations.tsx';
import Integrations from '@/pages/Integrations/Integrations.tsx';
import AutomationPage from '@/pages/AutomationPage/AutomationPage.tsx';
import EditorPage from '@/pages/Editor/Editor.tsx';
import AutomationReleasePage from '@/pages/Editor/AutomationReleasePage.tsx';
import RunPage from '@/pages/Run/RunPage.tsx';
import ResultPage from '@/pages/Result/ResultPage.tsx';
import RunPageEmpty from '@/pages/Run/RunPageEmpty.tsx';
import FullPageSpinner from '@/components/FullPageSpinner.tsx';
import { Center, Flex } from '@mantine/core';
import { useOrgProject } from '@/context/OrgProjectContext/useOrgProject.ts';
import LeftSidebarWrapper from '@/layouts/LeftSidebarWrapper.tsx';
import AutomationLayout from '@/layouts/AutomationLayout.tsx';
import StyleGuide from '@/pages/Styleguide/Styleguide.tsx';
import RunsLayout from '@/layouts/RunsLayout.tsx';
import E2EPublishReviewHarness from '@/pages/Styleguide/E2EPublishReviewHarness.tsx';
import E2ENestedRunOverflowHarness from '@/pages/Styleguide/E2ENestedRunOverflowHarness.tsx';
import E2EScopeNavigatorViewportHarness from '@/pages/Styleguide/E2EScopeNavigatorViewportHarness.tsx';
import HumanDecisionsInbox from '@/pages/HumanDecisions/HumanDecisionsInbox.tsx';
import AutomationSuites from '@/pages/AutomationSuites/AutomationSuites.tsx';
import AutomationSuite from '@/pages/AutomationSuite/AutomationSuite.tsx';

const E2EPdfViewerHarness =
  import.meta.env.MODE === 'e2e'
    ? lazy(() => import('@/pages/Styleguide/E2EPdfViewerHarness.tsx'))
    : null;

const E2EStaticDatasetUsageHarness =
  import.meta.env.MODE === 'e2e'
    ? lazy(() => import('@/pages/Styleguide/E2EStaticDatasetUsageHarness.tsx'))
    : null;

function Router() {
  const { loading, error } = useOrgProject();

  if (loading) return <FullPageSpinner />;
  if (error) {
    return (
      <Center style={{ width: '100vw', height: '100vh' }}>
        <Flex>Error: {error.message}</Flex>
      </Center>
    );
  }

  return (
    <LeftSidebarWrapper>
      <Routes>
        {import.meta.env.MODE === 'e2e' && E2EPdfViewerHarness && (
          <>
            {E2EStaticDatasetUsageHarness && (
              <Route
                path="/e2e/static-dataset-usage/:automationId"
                element={
                  <Suspense fallback={<FullPageSpinner />}>
                    <E2EStaticDatasetUsageHarness />
                  </Suspense>
                }
              />
            )}
            <Route path="/e2e/version-review/:automationId" element={<E2EPublishReviewHarness />} />
            <Route path="/e2e/standard-publish/:automationId" element={<E2EPublishReviewHarness />} />
            <Route path="/e2e/nested-run-overflow" element={<E2ENestedRunOverflowHarness />} />
            <Route
              path="/e2e/scope-navigator-viewport"
              element={<E2EScopeNavigatorViewportHarness />}
            />
            <Route
              path="/e2e/pdf-viewer"
              element={
                <Suspense fallback={<FullPageSpinner />}>
                  <E2EPdfViewerHarness />
                </Suspense>
              }
            />
          </>
        )}
        {import.meta.env.VITE_ENV === 'development' && (
          <Route path="/styleguide" element={<StyleGuide />} />
        )}
        <Route path="/logout" element={<Logout />} />

        <Route path="/automations" element={<Automations />} />
        <Route path="/automation-suites" element={<AutomationSuites />} />
        <Route path="/automation-suites/:suiteId" element={<AutomationSuite />} />
        <Route path="/human-decisions" element={<HumanDecisionsInbox />} />

        <Route path="/automations/:automationId" element={<AutomationLayout />}>
          <Route index element={<AutomationPage />} />
          <Route path="editor" element={<EditorPage />} />
          <Route path="release" element={<AutomationReleasePage />} />

          <Route path="runs" element={<RunsLayout />}>
            <Route index element={<RunPageEmpty />} />
            <Route path=":runId/*" element={<RunPage />} />
          </Route>

          <Route path="results/:runId/*" element={<ResultPage />} />
          <Route path="*" element={<Navigate to="." replace />} />
        </Route>

        <Route path="/integrations" element={<Integrations />} />
        <Route path="/" element={<Navigate to="/automations" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </LeftSidebarWrapper>
  );
}

export default Router;
